import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { formatDualCurrency } from '../utils/currency';
import { SecondaryPrice } from '../components/SecondaryPrice';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { MapPin, Tag, ChatCircle, Trash, ShareFat, Star, WhatsappLogo, SealCheck, FacebookLogo, Eye, Storefront, User as UserIcon, Link as LinkIcon, Copy, Flag, CaretLeft, CaretRight, CaretDown, CaretUp, X, ArrowClockwise, Rocket, DotsThreeVertical, EyeSlash, CheckCircle, Phone, SignIn } from '@phosphor-icons/react';
import { LazyImage } from '../components/LazyImage';
import { isEmailNotVerifiedError, showEmailVerificationRequiredToast } from '../utils/emailVerification';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { FavoriteButton } from '../components/FavoriteButton';
import { FeaturedBadge } from '../components/FeaturedBadge';
import { ListingStatusBadge } from '../components/ListingStatusBadge';
import { SellerName } from '../components/SellerName';
import { ImageLightbox } from '../components/listing/ImageLightbox';
import { getListingDurationDays } from '../utils/platformConfig';
import { useTranslation } from 'react-i18next';
import { getCategoryName, getSubcategoryName } from '../utils/categoryNames';
import { fetchSellerMessageLanguage, buildWhatsappContactUrl } from '../utils/whatsappContact';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ListingDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  // Toggle for the internal messaging section — closed by default, opens
  // via the "Internal message" contact button on the seller card.
  const [messageOpen, setMessageOpen] = useState(false);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [viewCount, setViewCount] = useState(0);
  const [similarListings, setSimilarListings] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [reporting, setReporting] = useState(false);
  // Review eligibility + form state
  const [canReview, setCanReview] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const REPORT_REASONS = [
    { key: 'fraud_scam', label: t('report.fraudScam') },
    { key: 'inappropriate_content', label: t('report.inappropriateContent') },
    { key: 'wrong_category', label: t('report.wrongCategory') },
    { key: 'duplicate', label: t('report.duplicate') },
    { key: 'spam', label: t('report.spam') },
    { key: 'other', label: t('report.other') },
  ];

  const fetchListing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${id}`);
      setListing(data);
      setViewCount(data.view_count || 0);

      // Save to recently-viewed (localStorage) — fire & forget
      try {
        const { addRecentlyViewed } = await import('../utils/recentlyViewed');
        addRecentlyViewed(data);
      } catch (err) { console.debug('recentlyViewed save failed:', err); }

      // Record view + fetch seller + similar in parallel
      const promises = [
        axios.post(`${API}/listings/${id}/view`).catch(() => null),
        data.user_id ? axios.get(`${API}/users/${data.user_id}/profile`, { withCredentials: true }).catch(() => null) : null,
        axios.get(`${API}/listings/${id}/similar?limit=5`).catch(() => null),
        data.category && data.subcategory ? axios.get(`${API}/category-fields/${data.category}/${data.subcategory}`).catch(() => null) : null,
      ];
      const [viewRes, sellerRes, similarRes, fieldDefsRes] = await Promise.all(promises);
      if (viewRes?.data) setViewCount(viewRes.data.view_count);
      if (sellerRes?.data) setSellerInfo(sellerRes.data);
      if (similarRes?.data) setSimilarListings(similarRes.data);
      if (fieldDefsRes?.data?.fields) setFieldDefs(fieldDefsRes.data.fields);
    } catch (error) {
      toast.error(t('listing.notFound'));
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  // Fetch review eligibility (silent). Only meaningful if user is logged in.
  useEffect(() => {
    if (!user || !id) { setCanReview(false); return; }
    let cancelled = false;
    // eslint-disable-next-line no-console
    console.debug('[review-eligibility] request', {
      currentUserId: user?.user_id,
      listingId: id,
      sellerId: listing?.user_id,
    });
    axios
      .get(`${API}/reviews/eligibility/${id}`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.debug('[review-eligibility] response', res.data);
        setCanReview(!!res.data?.can_review);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.debug('[review-eligibility] error', err?.response?.status, err?.response?.data);
        setCanReview(false);
      });
    return () => { cancelled = true; };
  }, [user, id, listing?.user_id]);

  const handleSubmitReview = async () => {
    if (!reviewRating) { toast.error(t('review.selectRating')); return; }
    if (!listing) return;
    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        seller_id: listing.user_id,
        listing_id: listing.listing_id,
        rating: reviewRating,
        comment: reviewComment || null,
      }, { withCredentials: true });
      toast.success(t('review.reviewSubmitted'));
      setReviewOpen(false);
      setReviewRating(0);
      setReviewHover(0);
      setReviewComment('');
      setCanReview(false); // one-per-listing — button goes away
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!user) { navigate('/login'); return; }
    if (!message.trim()) { toast.error(t('messages.enterMessage')); return; }
    setSending(true);
    try {
      await axios.post(
        `${API}/messages`,
        { listing_id: listing.listing_id, receiver_id: listing.user_id, content: message },
        { withCredentials: true },
      );
      toast.success(t('messages.sent'));
      setMessage('');
      setMessageOpen(false);

      // Keep the user in context: don't navigate away. Scroll the seller card
      // (and the "Leave a review" button) into view so the next logical
      // action is one tap away.
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="seller-card"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Refresh review eligibility — with a fresh message the server gate now
      // allows reviews, so the button should reflect the true state.
      try {
        const { data } = await axios.get(`${API}/reviews/eligibility/${id}`, { withCredentials: true });
        setCanReview(!!data?.can_review);
      } catch (err) {
        // Swallow — fallback visibility still applies
        console.debug('eligibility refresh after send failed', err?.message);
      }
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        showEmailVerificationRequiredToast(t);
      } else {
        toast.error(t('messages.sendFailed'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/listings/${id}`, { withCredentials: true });
      toast.success(t('listing.deleted'));
      navigate('/my-listings');
    } catch (error) {
      toast.error(t('listing.deleteFailed'));
    }
  };

  const handleRenew = async () => {
    try {
      const { data } = await axios.post(`${API}/listings/${id}/renew`, {}, { withCredentials: true });
      const days = data?.duration_days ?? await getListingDurationDays();
      toast.success(t('listing.renewed', { days }));
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.renewFailed'));
    }
  };

  const handleMarkSold = async () => {
    try {
      await axios.post(`${API}/listings/${id}/mark-sold`, {}, { withCredentials: true });
      toast.success(t('listing.markedSold'));
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.markSoldFailed'));
    }
  };

  const handleHide = async () => {
    try {
      await axios.post(`${API}/listings/${id}/hide`, {}, { withCredentials: true });
      toast.success(t('listing.hiddenSuccess'));
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.hideFailed'));
    }
  };

  const handleActivate = async () => {
    try {
      await axios.post(`${API}/listings/${id}/activate`, {}, { withCredentials: true });
      toast.success(t('listing.reactivatedSuccess'));
      fetchListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.reactivateFailed'));
    }
  };

  const handleWhatsAppShare = () => {
    const listingUrl = window.location.href;
    const text = `${listing.title} - ${formatDualCurrency(listing.price_mga).mga} - ${listingUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleFacebookShare = () => {
    const listingUrl = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(listingUrl)}`, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t('listing.linkCopied'));
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleReport = async () => {
    if (!reportReason) { toast.error(t('report.selectReason')); return; }
    setReporting(true);
    try {
      await axios.post(`${API}/listings/${id}/report`, { reason: reportReason, comment: reportComment }, { withCredentials: true });
      toast.success(t('report.submitted'));
      setReportOpen(false);
      setReportReason('');
      setReportComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('report.failed'));
    } finally {
      setReporting(false);
    }
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return null;
    const diff = Date.now() - new Date(lastActive).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return t('review.lastActiveToday');
    if (hours < 24) return `${t('review.lastActive')}: ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return `${t('review.lastActive')}: 1 day ago`;
    return `${t('review.lastActive')}: ${days} days ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text_muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="text-center py-12" data-testid="listing-not-found">
          <p className="text-text_muted">{t('listing.notFound')}</p>
        </div>
      </div>
    );
  }

  const price = formatDualCurrency(listing.price_mga, secondaryCurrency);
  const isOwner = user && user.user_id === listing.user_id;

  return (
    <div className={`min-h-screen bg-cream ${!isOwner ? 'pb-40 md:pb-0' : 'pb-20 md:pb-0'}`}>
      <Header />

      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="listing-detail">
          {listing.images && listing.images.length > 0 && (
            <div className="relative" data-testid="image-carousel">
              {listing.is_featured && (
                <div className="absolute top-4 left-4 z-20"><FeaturedBadge /></div>
              )}
              <Carousel className="w-full" opts={{ loop: true }}>
                <CarouselContent>
                  {listing.images.map((img, idx) => (
                    <CarouselItem key={img}>
                      <div className="aspect-video bg-surface cursor-pointer" onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}>
                        <LazyImage src={`${API}/files/${img}`} alt={`${listing.title} - ${idx + 1}`} className="w-full h-full" style={{ objectFit: 'contain' }} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {listing.images.length > 1 && (
                  <>
                    <CarouselPrevious className="left-4" />
                    <CarouselNext className="right-4" />
                  </>
                )}
              </Carousel>
            </div>
          )}

          <div className="p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-2 md:gap-4">
              <div className="flex-1 min-w-0 w-full md:w-auto">
                <h1
                  className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2 line-clamp-2 md:line-clamp-none md:whitespace-normal md:overflow-visible"
                  title={listing.title}
                  data-testid="listing-title"
                >
                  {listing.title}
                </h1>

                {/* ─── Tablet / desktop (>= 768px): original expanded layout ─── */}
                <div className="hidden md:block">
                  <div className="flex gap-4 text-sm text-text_muted mb-2 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin size={16} /> {listing.city}</span>
                    <Link
                      to={`/browse?category=${encodeURIComponent(listing.category)}`}
                      className="flex items-center gap-1 hover:underline"
                      data-testid="detail-meta-category-desktop"
                    >
                      <Tag size={16} /> <span className="font-semibold text-primary">{getCategoryName(listing.category, t)}</span>
                    </Link>
                    {listing.subcategory && (
                      <Link
                        to={`/browse?category=${encodeURIComponent(listing.category)}&subcategory=${encodeURIComponent(listing.subcategory)}`}
                        className="flex items-center gap-1 hover:text-primary hover:underline"
                        data-testid="listing-subcategory-desktop"
                      >
                        <span className="opacity-60">·</span>
                        <span className="font-medium text-text_main">
                          {getSubcategoryName(listing.subcategory, t)}
                        </span>
                      </Link>
                    )}
                  </div>
                  {/* Posted date + views */}
                  <div className="flex gap-3 text-sm text-text_muted flex-wrap" data-testid="listing-meta">
                    {listing.created_at && (
                      <span>{t('review.postedAgo')} {formatDistanceToNow(new Date(listing.created_at), { addSuffix: true })}</span>
                    )}
                    <span className="flex items-center gap-1" data-testid="view-count">
                      <Eye size={14} /> {viewCount} {t('review.views')}
                    </span>
                  </div>
                  {/* Seller type badge */}
                  {listing.seller_type && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="seller-type-badge">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                        listing.seller_type === 'business'
                          ? 'bg-info-soft text-info'
                          : 'bg-surface_muted text-text_main'
                      }`}>
                        {listing.seller_type === 'business' ? <Storefront size={13} weight="fill" /> : <UserIcon size={13} weight="fill" />}
                        {t('sellerType.label')}: {t(`sellerType.${listing.seller_type}`)}
                      </span>
                      <ListingStatusBadge listing={listing} />
                    </div>
                  )}
                  {!listing.seller_type && (
                    <div className="mt-2">
                      <ListingStatusBadge listing={listing} />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1 md:gap-2 shrink-0 flex-nowrap">
                {!isOwner && (
                  <>
                    <FavoriteButton listingId={listing.listing_id} className="shadow-md h-8 w-8 md:h-9 md:w-9" />
                    {/* Mobile-only share icons: keep WhatsApp/Facebook/Copy in the
                        top row because there is no dedicated share section below on mobile.
                        Desktop/tablet render the dedicated "Share this listing" section further down. */}
                    <Button variant="ghost" size="icon" onClick={handleWhatsAppShare} className="md:hidden h-8 w-8 text-text_muted hover:text-success hover:bg-success-soft" data-testid="whatsapp-share-button" title={t('listing.shareWhatsapp')}>
                      <WhatsappLogo size={16} weight="fill" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleFacebookShare} className="md:hidden h-8 w-8 text-text_muted hover:text-info hover:bg-info-soft" data-testid="facebook-share-button" title={t('listing.shareFacebook')}>
                      <FacebookLogo size={16} weight="fill" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleCopyLink} className="md:hidden h-8 w-8 text-text_muted hover:text-primary hover:bg-primary/10" data-testid="copy-link-button" title={t('listing.copyLink')}>
                      <Copy size={16} />
                    </Button>
                    {user && (
                      <Button variant="ghost" size="icon" onClick={() => setReportOpen(true)} className="h-8 w-8 md:h-9 md:w-9 text-text_muted hover:text-danger hover:bg-danger-soft" data-testid="report-listing-button" title={t('report.reportListing')}>
                        <Flag size={16} className="md:hidden" />
                        <Flag size={18} className="hidden md:block" />
                      </Button>
                    )}
                  </>
                )}
                {isOwner && (
                  <>
                    {listing.status === 'expired' ? (
                      <>
                        <Button
                          onClick={handleRenew}
                          variant="success"
                          className="gap-1.5"
                          data-testid="repost-listing-button"
                        >
                          <ArrowClockwise size={16} weight="bold" />
                          {t('listing.repost')}
                        </Button>
                        <Button
                          variant="outline"
                          disabled
                          title={t('listing.promoteSoon')}
                          className="gap-1.5 opacity-60 cursor-not-allowed"
                          data-testid="promote-placeholder-button"
                        >
                          <Rocket size={16} weight="bold" />
                          {t('listing.promote')}
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={() => navigate(`/edit/${listing.listing_id}`)} data-testid="edit-listing-button">
                        {t('listing.edit')}
                      </Button>
                    )}

                    {/* Overflow menu — items depend on listing.status (active / hidden / sold).
                        Expired flow uses its own inline Reposter/Promouvoir layout, so the overflow
                        is skipped for expired listings. */}
                    {listing.status !== 'expired' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9"
                            aria-label={t('listing.moreActions')}
                            data-testid="owner-overflow-menu-trigger"
                          >
                            <DotsThreeVertical size={20} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56" data-testid="owner-overflow-menu">
                          {listing.status === 'active' && (
                            <>
                              <DropdownMenuItem
                                disabled
                                className="gap-2 opacity-60 cursor-not-allowed"
                                data-testid="menu-promote"
                              >
                                <Rocket size={16} weight="bold" />
                                <span>{t('listing.promote')}</span>
                                <span className="ml-auto text-[10px] text-text_muted">{t('common.soon')}</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); handleHide(); }}
                                className="gap-2"
                                data-testid="menu-hide"
                              >
                                <EyeSlash size={16} weight="bold" />
                                <span>{t('listing.hide')}</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); handleMarkSold(); }}
                                className="gap-2"
                                data-testid="menu-mark-sold"
                              >
                                <CheckCircle size={16} weight="bold" className="text-success" />
                                <span>{t('listing.markSold')}</span>
                              </DropdownMenuItem>
                            </>
                          )}
                          {listing.status === 'hidden' && (
                            <>
                              <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); handleActivate(); }}
                                className="gap-2"
                                data-testid="menu-unhide"
                              >
                                <Eye size={16} weight="bold" className="text-primary" />
                                <span>{t('listing.unhide')}</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); handleMarkSold(); }}
                                className="gap-2"
                                data-testid="menu-mark-sold"
                              >
                                <CheckCircle size={16} weight="bold" className="text-success" />
                                <span>{t('listing.markSold')}</span>
                              </DropdownMenuItem>
                            </>
                          )}
                          {listing.status === 'sold' && (
                            <DropdownMenuItem
                              onSelect={(e) => { e.preventDefault(); handleActivate(); }}
                              className="gap-2"
                              data-testid="menu-reactivate"
                            >
                              <ArrowClockwise size={16} weight="bold" className="text-primary" />
                              <span>{t('listing.reactivate')}</span>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={(e) => { e.preventDefault(); setDeleteOpen(true); }}
                            className="gap-2 text-danger focus:text-danger focus:bg-danger-soft"
                            data-testid="menu-delete"
                          >
                            <Trash size={16} weight="bold" />
                            <span>{t('listing.delete')}</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    {/* Delete confirmation dialog — controlled, triggered from the overflow menu */}
                    <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('listing.deleteConfirmTitle')}</AlertDialogTitle>
                          <AlertDialogDescription>{t('listing.deleteConfirmDesc')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDelete}>{t('listing.delete')}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>

            {/* ─── Mobile portrait (< 768px): full-width 2-row meta stack ─── */}
            <div className="md:hidden mb-4" data-testid="mobile-meta-block">
              {/* Line 1: 📍 city · 📦 category · subcategory (full width, category not cramped) */}
              <div
                className="flex items-center gap-1.5 text-xs text-text_muted whitespace-nowrap overflow-hidden mb-1.5"
                data-testid="listing-meta-mobile"
              >
                <MapPin size={13} weight="regular" className="shrink-0" />
                <span className="truncate">{listing.city}</span>
                <span className="shrink-0 opacity-60">·</span>
                <Link
                  to={`/browse?category=${encodeURIComponent(listing.category)}`}
                  className="flex items-center gap-1 min-w-0 hover:underline"
                  data-testid="detail-meta-category-mobile"
                >
                  <Tag size={13} weight="regular" className="shrink-0 text-primary" />
                  <span className="font-semibold text-primary truncate">
                    {getCategoryName(listing.category, t)}
                  </span>
                </Link>
                {listing.subcategory && (
                  <>
                    <span className="shrink-0 opacity-60">·</span>
                    <Link
                      to={`/browse?category=${encodeURIComponent(listing.category)}&subcategory=${encodeURIComponent(listing.subcategory)}`}
                      className="font-medium text-text_main truncate hover:text-primary hover:underline"
                      data-testid="listing-subcategory-mobile"
                    >
                      {getSubcategoryName(listing.subcategory, t)}
                    </Link>
                  </>
                )}
              </div>
              {/* Line 2: Publié X · 👁 X · [seller-type badge] · [status badge] */}
              <div
                className="flex items-center gap-1.5 text-xs text-text_muted whitespace-nowrap overflow-hidden"
                data-testid="listing-secondary-mobile"
              >
                {listing.created_at && (
                  <span>
                    {t('listing.postedShort', { defaultValue: 'Publié' })}{' '}
                    {formatDistanceToNow(new Date(listing.created_at))}
                  </span>
                )}
                <span className="shrink-0 opacity-60">·</span>
                <span className="flex items-center gap-1 shrink-0" data-testid="view-count-mobile">
                  <Eye size={12} /> {viewCount}
                </span>
                {listing.seller_type && (
                  <>
                    <span className="shrink-0 opacity-60">·</span>
                    <span
                      className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        listing.seller_type === 'business'
                          ? 'bg-info-soft text-info'
                          : 'bg-surface_muted text-text_main'
                      }`}
                      data-testid="seller-type-badge-mobile"
                    >
                      {listing.seller_type === 'business' ? <Storefront size={12} weight="fill" /> : <UserIcon size={12} weight="fill" />}
                      {t(`sellerType.${listing.seller_type}`)}
                    </span>
                  </>
                )}
                <span className="shrink-0 ml-auto">
                  <ListingStatusBadge listing={listing} />
                </span>
              </div>
            </div>

            {/* Price + Price Type */}
            <div className="mb-6">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-3xl font-bold text-primary" data-testid="listing-price-mga">{price.mga}</p>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${listing.price_type === 'negotiable' ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'}`} data-testid="price-type-badge">
                  {listing.price_type === 'negotiable' ? t('listing.priceNegotiable') : t('listing.priceFixed')}
                </span>
              </div>
              <SecondaryPrice formattedPrice={price.fmg} className="text-lg mt-1" />
            </div>

            {/* Vehicle Details */}
            {listing.category === 'vehicles' && (() => {
              // Merge vehicle_details with legacy flat fields
              const vd = { ...(listing.vehicle_details || {}) };
              if (!vd.make && listing.brand) vd.make = listing.brand;
              if (!vd.year && listing.year) vd.year = listing.year;
              if (!vd.fuel_type && listing.fuel_type) vd.fuel_type = listing.fuel_type;
              if (!vd.transmission && listing.transmission) vd.transmission = listing.transmission;
              if (!vd.mileage && listing.mileage) vd.mileage = listing.mileage;
              if (!vd.condition && listing.condition) vd.condition = listing.condition;

              const fieldLabels = {
                make: t('vehicle.brand'), model: 'Model', year: t('vehicle.year'),
                mileage: t('vehicle.mileage'), body_type: 'Body Type', num_seats: 'Seats',
                registered: 'Registered', fuel_type: t('vehicle.fuelType'), engine: 'Engine',
                transmission: t('vehicle.transmission'), drivetrain: 'Drivetrain',
                condition: t('vehicle.condition'), van_type: 'Van Type', load_capacity: 'Load Capacity',
                usage_type: 'Usage', tuktuk_body: 'Body Style', moto_type: 'Type',
                engine_cc: 'Engine', start_type: 'Start', cooling: 'Cooling',
                scooter_type: 'Type', range_km: 'Range', truck_type: 'Truck Type',
                tonnage: 'Tonnage', num_axles: 'Axles', bus_usage: 'Usage',
                tractor_drive: 'Drive', tractor_usage: 'Usage', equipment_type: 'Equipment',
                power_hp: 'Power', operating_hours: 'Hours', weight_kg: 'Weight',
                trailer_type: 'Trailer Type', dimensions: 'Dimensions', atv_type: 'ATV Type',
                boat_type: 'Boat Type', boat_engine: 'Engine', passenger_capacity: 'Passengers',
                boat_material: 'Material', length_m: 'Length'
              };
              const unitMap = { mileage: 'km', engine_cc: 'cc', load_capacity: 'kg', range_km: 'km', tonnage: 't', power_hp: 'HP', operating_hours: 'h', weight_kg: 'kg', length_m: 'm' };

              const entries = Object.entries(vd).filter(([_, v]) => v != null && v !== '');
              if (entries.length === 0) return null;

              return (
                <div className="mb-6" data-testid="vehicle-details-section">
                  <h2 className="text-base font-medium text-text_strong tracking-wide mb-3">{t('vehicle.details')}</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {entries.map(([key, value]) => (
                      <div key={key} className="bg-cream rounded-lg px-4 py-3" data-testid={`vehicle-${key}`}>
                        <p className="text-xs text-text_muted mb-0.5">{fieldLabels[key] || key}</p>
                        <p className="text-sm font-semibold text-text_main">
                          {typeof value === 'number' ? value.toLocaleString() : value}{unitMap[key] ? ` ${unitMap[key]}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Real Estate Details */}
            {listing.category === 'real_estate' && (listing.transaction_type || listing.property_type || listing.surface_area || listing.living_area || listing.land_area || listing.num_rooms) && (
              <div className="mb-6" data-testid="real-estate-details-section">
                <h2 className="text-base font-medium text-text_strong tracking-wide mb-3">{t('realEstate.details')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {listing.transaction_type && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-transaction-type">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.transactionType')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.transaction_type}`)}</p>
                    </div>
                  )}
                  {listing.property_type && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-property-type">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.propertyType')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.property_type}`)}</p>
                    </div>
                  )}
                  {listing.property_condition && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-condition">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.condition')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.property_condition}`)}</p>
                    </div>
                  )}
                  {listing.furnished && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-furnished">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.furnished')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.furnished}`)}</p>
                    </div>
                  )}
                  {listing.surface_area != null && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-surface-area">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.surfaceArea')}</p>
                      <p className="text-sm font-semibold text-text_main">{listing.surface_area.toLocaleString()} m²</p>
                    </div>
                  )}
                  {listing.living_area != null && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-living-area">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.livingArea')}</p>
                      <p className="text-sm font-semibold text-text_main">{listing.living_area.toLocaleString()} m²</p>
                    </div>
                  )}
                  {listing.land_area != null && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-land-area">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.landArea')}</p>
                      <p className="text-sm font-semibold text-text_main">{listing.land_area.toLocaleString()} m²</p>
                    </div>
                  )}
                  {listing.num_rooms != null && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-num-rooms">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.numRooms')}</p>
                      <p className="text-sm font-semibold text-text_main">{listing.num_rooms}</p>
                    </div>
                  )}
                  {listing.land_type && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-land-type">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.landType')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.land_type}`)}</p>
                    </div>
                  )}
                  {listing.land_usage && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-land-usage">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.landUsage')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.land_usage}`)}</p>
                    </div>
                  )}
                  {listing.space_type && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="re-space-type">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.spaceType')}</p>
                      <p className="text-sm font-semibold text-text_main">{t(`realEstate.${listing.space_type}`)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Location — Region + City */}
            {(listing.region || listing.city) && (
              <div className="mb-6" data-testid="location-section">
                <h2 className="text-base font-medium text-text_strong tracking-wide mb-3">{t('listing.location')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {listing.region && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="listing-region">
                      <p className="text-xs text-text_muted mb-0.5">{t('realEstate.region')}</p>
                      <p className="text-sm font-semibold text-text_main">{listing.region}</p>
                    </div>
                  )}
                  {listing.city && (
                    <div className="bg-cream rounded-lg px-4 py-3" data-testid="listing-city">
                      <p className="text-xs text-text_muted mb-0.5">{t('listing.city')}</p>
                      <p className="text-sm font-semibold text-text_main">{listing.city}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom Fields (admin-defined) */}
            {listing.custom_fields && Object.keys(listing.custom_fields).length > 0 && (
              <div className="mb-6" data-testid="custom-fields-display">
                <h2 className="text-base font-medium text-text_strong tracking-wide mb-3">{t('listing.filters')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(() => {
                    const cf = listing.custom_fields;
                    // Use field definitions order if available, otherwise fallback to object keys
                    const orderedKeys = fieldDefs.length > 0
                      ? fieldDefs.map(fd => fd.key).filter(k => k in cf)
                      : Object.keys(cf);
                    // Add any keys in custom_fields not in fieldDefs (legacy data)
                    const extraKeys = Object.keys(cf).filter(k => !orderedKeys.includes(k));
                    return [...orderedKeys, ...extraKeys].map(key => {
                      const value = cf[key];
                      if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
                      const displayVal = Array.isArray(value) ? value.join(', ') : String(value);
                      const def = fieldDefs.find(fd => fd.key === key);
                      const displayLabel = def?.label || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      return (
                        <div key={key} className="bg-cream rounded-lg px-4 py-3" data-testid={`custom-field-${key}`}>
                          <p className="text-xs text-text_muted mb-0.5">{displayLabel}</p>
                          <p className="text-sm font-semibold text-text_main">{displayVal}</p>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="mb-6">
              <h2 className="text-base font-medium text-text_strong tracking-wide mb-2">{t('listing.description')}</h2>
              <p className="text-text_main leading-relaxed whitespace-pre-wrap" data-testid="listing-description">{listing.description}</p>
            </div>

            {/* Share Section (desktop/tablet only — mobile keeps the icons in the top row) */}
            {!isOwner && (
              <div className="hidden md:block mb-6 border-t border-border pt-6" data-testid="bottom-share-section">
                <p className="text-sm font-medium text-text_muted mb-3 tracking-wide">{t('listing.shareThisListing')}</p>
                <div className="flex gap-3 flex-wrap items-center">
                  <Button variant="outline" onClick={handleWhatsAppShare} className="gap-2 border-success text-success hover:bg-success-soft hover:border-success px-5" data-testid="bottom-whatsapp-share">
                    <WhatsappLogo size={18} weight="fill" /> WhatsApp
                  </Button>
                  <Button variant="outline" onClick={handleFacebookShare} className="gap-2 border-info text-info hover:bg-info-soft hover:border-info px-5" data-testid="bottom-facebook-share">
                    <FacebookLogo size={18} weight="fill" /> Facebook
                  </Button>
                  <Button variant="outline" onClick={handleCopyLink} className="gap-2 border-border text-text_muted hover:bg-surface hover:border-primary hover:text-primary px-5" data-testid="bottom-copy-link">
                    <Copy size={18} /> {t('listing.copyLink')}
                  </Button>
                  {user && listing && user.user_id !== listing.user_id && (
                    <>
                      {/* Divider separating "share" actions from the review CTA */}
                      <span
                        aria-hidden="true"
                        className="inline-block w-px h-6 bg-border/70 mx-1"
                        data-testid="share-review-divider"
                      />
                      <Button
                        variant="outline"
                        onClick={() => setReviewOpen(true)}
                        className="gap-2 border-warning text-warning hover:bg-warning/10 hover:border-warning px-5"
                        data-testid="leave-review-btn"
                      >
                        <Star size={18} weight="fill" /> {t('review.writeReview')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Mobile-only Review button (the desktop/tablet Share section is
                hidden on <md — the review CTA needs a home there too). */}
            {!isOwner && user && listing && user.user_id !== listing.user_id && (
              <div className="md:hidden mb-6 border-t border-border pt-4 flex justify-start" data-testid="mobile-review-cta">
                <Button
                  variant="outline"
                  onClick={() => setReviewOpen(true)}
                  className="gap-2 border-warning text-warning hover:bg-warning/10 hover:border-warning px-4 py-1.5 text-sm"
                  data-testid="leave-review-btn-mobile"
                >
                  <Star size={16} weight="fill" /> {t('review.writeReview')}
                </Button>
              </div>
            )}

            {/* Seller Card */}
            <div className="border-t border-border pt-6 mb-6" data-testid="seller-card">
              <h3 className="text-lg font-semibold font-heading mb-4">{t('review.seller')}</h3>
              <div className="bg-cream rounded-xl p-5">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 mb-4">
                  {/* Left column — seller avatar (mini-map removed iter 76:
                      the location text just below already conveys the same
                      info without extra weight or tile fetches). */}
                  <div className="flex flex-col items-center gap-3 flex-shrink-0" data-testid="seller-avatar-map-stack">
                    <Link to={`/users/${listing.user_id}`}>
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-primary rounded-full flex items-center justify-center text-white text-3xl font-bold hover:opacity-90 transition overflow-hidden">
                        {sellerInfo?.picture ? (
                          <img src={sellerInfo.picture.startsWith('http') ? sellerInfo.picture : `${API}/files/${sellerInfo.picture}`} alt={listing.user_name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          listing.user_name?.charAt(0).toUpperCase() || 'U'
                        )}
                      </div>
                    </Link>
                  </div>
                  {/* Right column — name, rating, member, last-active, location text, WhatsApp pill */}
                  <div className="flex-1 min-w-0 w-full text-center sm:text-left">
                    <Link to={`/users/${listing.user_id}`} className="hover:underline block" data-testid="seller-name">
                      <div className="flex items-center gap-1.5 flex-wrap justify-center sm:justify-start">
                        <SellerName
                          user={
                            sellerInfo
                              ? { ...sellerInfo, name: sellerInfo.name || listing.user_name }
                              : { name: listing.user_name, seller_type: listing.seller_type }
                          }
                          variant="stack"
                          badgeSize="inline"
                          primaryClassName="font-bold text-text_main text-lg"
                          secondaryClassName="text-xs text-text_muted"
                          testIdPrefix="seller-display"
                        />
                        {sellerInfo?.is_verified && <SealCheck size={18} weight="fill" className="text-primary flex-shrink-0" />}
                        {sellerInfo?.sold_count > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-success-soft text-success whitespace-nowrap"
                            title={`${sellerInfo.sold_count} ${t('profile.soldCountTitle')}`}
                            data-testid="seller-sold-count-badge"
                          >
                            ✅ {sellerInfo.sold_count} {t(sellerInfo.sold_count === 1 ? 'profile.soldCount_one' : 'profile.soldCount_other')}
                          </span>
                        )}
                      </div>
                    </Link>
                    {sellerInfo && (
                      <>
                        <div className="flex items-center gap-1 justify-center sm:justify-start">
                          <Star size={16} weight="fill" className="text-warning" />
                          <span className="font-semibold text-sm">{sellerInfo.average_rating}</span>
                          <span className="text-xs text-text_muted">({sellerInfo.total_reviews} {t('review.reviews')})</span>
                        </div>
                        <p className="text-xs text-text_muted mt-0.5">
                          {t('review.memberSince')}: {sellerInfo.created_at ? new Date(sellerInfo.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : ''}
                        </p>
                        {sellerInfo.last_active && (
                          <p className="text-xs text-text_muted" data-testid="seller-last-active">
                            {formatLastActive(sellerInfo.last_active)}
                          </p>
                        )}
                        {(listing.city || listing.region) && (
                          <p className="text-xs text-text_muted flex items-center gap-1 mt-1 justify-center sm:justify-start" data-testid="seller-location-text">
                            <MapPin size={13} weight="fill" className="text-primary" />
                            {[listing.city, listing.region].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* iter 77 note: the "Write a review" button previously lived
                    here — it now sits in the "Share this listing" section
                    above so the seller card stays lean. The Facebook profile
                    link (external, distinct action) stays with the seller
                    info. */}
                {sellerInfo?.facebook_url && (
                  <div className="flex gap-2 flex-wrap">
                    <a href={sellerInfo.facebook_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="gap-1.5" data-testid="facebook-profile-btn">
                        <FacebookLogo size={16} weight="fill" className="text-info" /> {t('review.viewFacebook')}
                      </Button>
                    </a>
                  </div>
                )}

                {/* Contact options — inside the seller card, no duplicates
                    elsewhere on the page. Symmetric grid: 3 equal columns,
                    fixed 46px height. Each tile = colored pill icon + label.
                    Anonymous visitors see a single sign-in CTA instead. */}
                {!isOwner && user && (
                  <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-3 gap-1.5 sm:gap-2 items-stretch" data-testid="seller-contact-options">
                    {/* Phone */}
                    {sellerInfo?.phone ? (
                      <a
                        href={`tel:${sellerInfo.phone}`}
                        className="h-[46px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl bg-surface_muted hover:bg-surface_muted/70 transition-colors px-1.5 sm:px-2 min-w-0"
                        data-testid="seller-contact-phone"
                        aria-label={t('listing.contactByPhone')}
                      >
                        <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          <Phone size={13} weight="fill" />
                        </span>
                        <span className="text-[11px] sm:text-sm font-medium text-text_main truncate">
                          {t('listing.contactByPhone')}
                        </span>
                      </a>
                    ) : (
                      <div
                        className="h-[46px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl bg-surface_muted px-1.5 sm:px-2 opacity-50 cursor-not-allowed min-w-0"
                        data-testid="seller-contact-phone-unavailable"
                        title={t('listing.contactByPhone')}
                      >
                        <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          <Phone size={13} weight="fill" />
                        </span>
                        <span className="text-[11px] sm:text-sm font-medium text-text_muted truncate">
                          {t('listing.contactByPhone')}
                        </span>
                      </div>
                    )}

                    {/* WhatsApp */}
                    {sellerInfo?.whatsapp_enabled && sellerInfo?.phone ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const lang = await fetchSellerMessageLanguage(listing.user_id);
                          const url = buildWhatsappContactUrl({
                            phone: sellerInfo.phone,
                            title: listing.title,
                            location: [listing.city, listing.region].filter(Boolean).join(', '),
                            price: formatDualCurrency(listing.price_mga, secondaryCurrency).mga,
                            language: lang,
                          });
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                        className="h-[46px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl bg-surface_muted hover:bg-surface_muted/70 transition-colors px-1.5 sm:px-2 min-w-0"
                        data-testid="seller-contact-whatsapp"
                        aria-label={t('listing.contactByWhatsapp')}
                      >
                        <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-success-soft flex items-center justify-center text-success shrink-0">
                          <WhatsappLogo size={13} weight="fill" />
                        </span>
                        <span className="text-[11px] sm:text-sm font-medium text-text_main truncate">
                          {t('listing.contactByWhatsapp')}
                        </span>
                      </button>
                    ) : (
                      <div
                        className="h-[46px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl bg-surface_muted px-1.5 sm:px-2 opacity-50 cursor-not-allowed min-w-0"
                        data-testid="seller-contact-whatsapp-unavailable"
                        title={t('listing.contactByWhatsapp')}
                      >
                        <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-success-soft flex items-center justify-center text-success shrink-0">
                          <WhatsappLogo size={13} weight="fill" />
                        </span>
                        <span className="text-[11px] sm:text-sm font-medium text-text_muted truncate">
                          {t('listing.contactByWhatsapp')}
                        </span>
                      </div>
                    )}

                    {/* Message (primary action, toggles the internal textarea).
                        Chevron is absolute-positioned in the top-right so it
                        stays discreet and doesn't steal horizontal space from
                        the label. */}
                    <button
                      type="button"
                      onClick={() => {
                        setMessageOpen((prev) => {
                          const next = !prev;
                          if (next) {
                            setTimeout(() => {
                              const el = document.getElementById('contact-section');
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              const ta = document.querySelector('[data-testid="message-input"]');
                              if (ta) ta.focus();
                            }, 80);
                          }
                          return next;
                        });
                      }}
                      className="relative h-[46px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl bg-primary hover:bg-primary-hover transition-colors px-1.5 sm:px-2 text-white min-w-0"
                      data-testid="seller-contact-message"
                      aria-expanded={messageOpen}
                      aria-controls="contact-section"
                      aria-label={t('listing.contactByMessage')}
                    >
                      <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 flex items-center justify-center text-white shrink-0">
                        <ChatCircle size={13} weight="fill" />
                      </span>
                      <span className="text-[11px] sm:text-sm font-medium text-white truncate">
                        {t('listing.contactByMessage')}
                      </span>
                      {messageOpen ? (
                        <CaretUp size={9} weight="bold" className="absolute top-1.5 right-1.5 text-white/60" data-testid="seller-contact-message-caret-up" />
                      ) : (
                        <CaretDown size={9} weight="bold" className="absolute top-1.5 right-1.5 text-white/60" data-testid="seller-contact-message-caret-down" />
                      )}
                    </button>
                  </div>
                )}

                {!isOwner && !user && (
                  <div className="mt-4 pt-4 border-t border-border/60 flex flex-col items-center gap-3 text-center" data-testid="seller-contact-login-cta">
                    <p className="text-sm text-text_muted">{t('listing.signInToContact')}</p>
                    <Button
                      onClick={() => navigate('/login')}
                      variant="primary"
                      className="font-semibold gap-2"
                      data-testid="seller-contact-signin-btn"
                    >
                      <SignIn size={16} weight="bold" />
                      {t('listing.signInOrRegister')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Seller — internal messaging textarea. Collapsible:
                closed by default, toggled open/closed by the "Internal
                message" pill in the seller card. No leftover whitespace when
                closed — the whole block is unmounted. No title inside — the
                textarea + Send button are shown directly. */}
            {!isOwner && user && messageOpen && (
              <div className="border-t border-border pt-6" id="contact-section" data-testid="message-section">
                <Textarea
                  placeholder={t('messages.typeMessage')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mb-3 border border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  rows={4}
                  data-testid="message-input"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending}
                  className="w-full bg-primary hover:bg-primary-hover text-white tracking-wide active:scale-95"
                  data-testid="send-message-button"
                >
                  <ChatCircle size={20} className="mr-2" weight="bold" />
                  {sending ? t('common.loading') : t('messages.send')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Similar Listings */}
        {similarListings.length > 0 && (
          <div className="mt-8" data-testid="similar-listings-section">
            <h2 className="text-base font-medium text-text_strong tracking-wide mb-4">{t('review.similarListings')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {similarListings.map((item) => (
                <Link
                  key={item.listing_id}
                  to={`/listings/${item.listing_id}`}
                  className="bg-white border border-border rounded-xl overflow-hidden hover:shadow-md transition group"
                  data-testid={`similar-listing-${item.listing_id}`}
                >
                  <div className="aspect-[4/3] bg-surface overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={`${API}/files/${item.images[0]}`} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text_muted text-xs">{t('common.noImage')}</div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-text_main truncate">{item.title}</p>
                    <p className="text-xs text-text_muted truncate">{item.city}</p>
                    <p className="text-sm font-bold text-primary mt-1">{formatDualCurrency(item.price_mga).mga}</p>
                    {item.created_at && (
                      <p className="text-xs text-text_muted mt-0.5">{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen && !!listing?.images?.length}
        images={listing?.images || []}
        index={lightboxIndex}
        title={listing?.title || ''}
        onClose={() => setLightboxOpen(false)}
        onIndex={setLightboxIndex}
      />

      {/* Report Listing Modal */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md" data-testid="report-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Flag size={20} className="text-danger" /> {t('report.reportListing')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-text_muted mb-2 block">{t('report.reason')}</Label>
              <div className="space-y-2">
                {REPORT_REASONS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setReportReason(key)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${
                      reportReason === key
                        ? 'border-danger bg-danger-soft text-danger font-medium'
                        : 'border-border hover:border-danger hover:bg-danger-soft/50 text-text_main'
                    }`}
                    data-testid={`report-reason-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-text_muted mb-1 block">{t('report.comment')}</Label>
              <Textarea
                placeholder={t('report.commentPlaceholder')}
                value={reportComment}
                onChange={(e) => setReportComment(e.target.value)}
                rows={3}
                className="border border-border"
                data-testid="report-comment-input"
              />
            </div>
            <Button
              onClick={handleReport}
              disabled={reporting || !reportReason}
              className="w-full bg-danger hover:bg-danger text-white"
              data-testid="report-submit-button"
            >
              <Flag size={16} className="mr-2" />
              {reporting ? t('common.loading') : t('report.submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave a review modal — seller_id + listing_id are prefilled from the
          current listing. Visible only when the backend `eligibility` endpoint
          said can_review=true. */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>{t('review.writeReview')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {listing && (
              <p className="text-sm text-text_muted" data-testid="review-dialog-listing-title">
                « {listing.title} »
              </p>
            )}
            <div>
              <Label className="text-sm">{t('review.rating') || 'Note'}</Label>
              <div className="flex gap-1 mt-1.5" data-testid="review-dialog-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setReviewHover(star)}
                    onMouseLeave={() => setReviewHover(0)}
                    onClick={() => setReviewRating(star)}
                    aria-label={`${star} star`}
                  >
                    <Star
                      size={32}
                      weight={(reviewHover || reviewRating) >= star ? 'fill' : 'regular'}
                      className={(reviewHover || reviewRating) >= star ? 'text-warning' : 'text-text_muted'}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">{t('review.comment')}</Label>
              <Textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={t('review.comment')}
                rows={3}
                className="mt-1 border border-border"
                maxLength={1000}
                data-testid="review-dialog-comment"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setReviewOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={reviewSubmitting}
                variant="primary"
                data-testid="review-dialog-submit"
              >
                {reviewSubmitting ? t('common.loading') : t('review.submitReview')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile floating action bar removed (iter 74) — Contact / WhatsApp /
          Favorite were duplicates of the seller card contact options and
          the heart in the top-right of the listing header. */}

      <BottomNav />
    </div>
  );
};
